<template>
    <Menu
        as="div"
        class="relative inline-block text-left"
        data-spec-class="user-dropdown"
    >
        <div>
            <MenuButton
                :class="{
                    'flex items-center transition-ease': true,
                    'text-gray-200 hover:text-gray-100': theme === LightDarkTheme.Dark,
                    'text-tertiary hover:text-muted': theme === LightDarkTheme.Light,
                }"
                data-spec-class="user-dropdown__toggle"
            >
                <span data-spec-class="user-dropdown__name">
                    <slot />
                </span>
                <span class="sr-only">Open options</span>
                <ChevronDownIcon
                    class="h-5 w-5"
                    aria-hidden="true"
                />
            </MenuButton>
        </div>
        <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
        >
            <MenuItems
                :class="{
                    'absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-md py-2 shadow-lg': true,
                    'bg-gray-800': theme === LightDarkTheme.Dark,
                    'bg-surface': theme === LightDarkTheme.Light,
                }"
                data-spec-class="user-dropdown__menu"
            >
                <div class="py-1">
                    <MenuItem
                        v-for="link in links"
                        :key="link.id"
                        data-spec-class="user-dropdown__menu-item"
                    >
                        <BaseLink
                            :class="{
                                'block px-3 py-1 text-sm/6 hover:no-underline': true,
                                '!text-gray-200 hover:bg-gray-700': theme === LightDarkTheme.Dark,
                                '!text-primary hover:!bg-surface--light':
                                    theme === LightDarkTheme.Light,
                            }"
                            :to="link.to"
                            :href="link.url"
                            data-spec-class="user-dropdown__menu-link"
                            :data-spec-id="`user-dropdown__menu-link--${link.id}`"
                        >
                            <div class="flex w-full items-center justify-between gap-x-2">
                                <span class="flex-1">
                                    {{ link.label }}
                                </span>

                                <BaseBadgeCounter
                                    v-if="link.badge?.isVisible"
                                    :theme="link.badge.theme"
                                    data-spec-class="user-dropdown__badge"
                                    :data-spec-id="`user-dropdown__badge--${link.id}`"
                                    :title="link.badge.title"
                                >
                                    {{ link.badge.value }}
                                </BaseBadgeCounter>
                            </div>
                        </BaseLink>
                    </MenuItem>
                </div>

                <div
                    v-if="$slots.additional"
                    class="py-1"
                >
                    <slot name="additional" />
                </div>

                <div class="py-1">
                    <MenuItem data-spec-class="user-dropdown__menu-item">
                        <BaseLinkLogout
                            :class="{
                                'block px-3 py-1 text-sm/6': true,
                                'text-gray-200 hover:bg-gray-700': theme === LightDarkTheme.Dark,
                                'text-primary hover:bg-surface--light':
                                    theme === LightDarkTheme.Light,
                            }"
                            data-spec-class="user-dropdown__menu-link"
                            data-spec-id="user-dropdown__menu-link--logout"
                        />
                    </MenuItem>
                </div>
            </MenuItems>
        </transition>
    </Menu>
</template>

<script setup lang="ts">
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import { LinkTo, LightDarkTheme } from '@/types';

defineProps<{
    links: LinkTo[];
    theme: LightDarkTheme;
}>();
</script>
